// Phase 9B — Board system styles.
//
// Monday.com-inspired operating system aesthetic. Premium terminal
// intelligence feel with structured data grids, status indicators,
// and priority signalling. Token-only — no hex, no rgb.
//
// Design language:
//   - Bebas Neue for board headers (--sz-font-display)
//   - JetBrains Mono for data/status (--sz-font-mono)
//   - DM Sans for descriptions (--sz-font-body)
//   - cardLow elevation for item cards
//   - Amber accent for active/selected states
//   - Left border priority stripe (critical=danger, high=amber, etc.)

import type { CSSProperties } from "react";
import { cardLow, cardMedium, cardLowSelected } from "../../styles/elevation.ts";
import { transitionStandard, transitionFast } from "../../styles/motion.ts";

// ─── Board shell ───────────────────────────────────────────────

export const boardRoot: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "var(--sz-space-4)",
  width: "100%",
};

export const boardHeader: CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "baseline",
  gap: "var(--sz-space-3)",
  paddingBottom: "var(--sz-space-2)",
  borderBottom: "2px solid var(--sz-amber)",
};

export const boardTitle: CSSProperties = {
  fontFamily: "var(--sz-font-display)",
  fontSize: "clamp(1.5rem, 3vw, 2.2rem)",
  letterSpacing: "2px",
  textTransform: "uppercase",
  color: "var(--sz-text-primary)",
  lineHeight: 1,
};

export const boardSubtitle: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-xs)",
  letterSpacing: "1.5px",
  textTransform: "uppercase",
  color: "var(--sz-text-muted)",
};

export const boardCount: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-sm)",
  color: "var(--sz-amber)",
  fontWeight: 600,
};

// ─── Status lane header (kanban column) ────────────────────────

export const laneHeader: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "var(--sz-space-2)",
  paddingBottom: "var(--sz-space-1)",
  borderBottom: "1px solid var(--sz-border-subtle)",
  marginBottom: "var(--sz-space-2)",
};

export const laneDot: CSSProperties = {
  width: "8px",
  height: "8px",
  borderRadius: "50%",
  flexShrink: 0,
};

export const laneLabel: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-xs)",
  letterSpacing: "1.5px",
  textTransform: "uppercase",
  color: "var(--sz-text-secondary)",
  fontWeight: 600,
};

export const laneCount: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-xs)",
  color: "var(--sz-text-muted)",
  marginLeft: "auto",
};

// ─── Board item card ───────────────────────────────────────────

export const itemCard: CSSProperties = {
  ...cardLow,
  ...transitionStandard,
  display: "flex",
  flexDirection: "column",
  gap: "var(--sz-space-2)",
  padding: "var(--sz-space-3) var(--sz-space-3) var(--sz-space-3) var(--sz-space-4)",
  borderRadius: "var(--sz-radius-md)",
  background: "var(--sz-surface-card)",
  borderLeft: "3px solid transparent",
  cursor: "pointer",
  position: "relative",
};

export const itemCardSelected: CSSProperties = {
  ...itemCard,
  ...cardLowSelected,
};

// Priority stripe variants (left border color)
export const priorityCritical: CSSProperties = {
  borderLeftColor: "var(--sz-danger)",
};

export const priorityHigh: CSSProperties = {
  borderLeftColor: "var(--sz-amber)",
};

export const priorityMedium: CSSProperties = {
  borderLeftColor: "var(--sz-info)",
};

export const priorityLow: CSSProperties = {
  borderLeftColor: "var(--sz-text-muted)",
};

export const priorityNone: CSSProperties = {
  borderLeftColor: "transparent",
};

// ─── Item card header ──────────────────────────────────────────

export const itemHeaderRow: CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: "var(--sz-space-2)",
};

export const itemTitle: CSSProperties = {
  fontFamily: "var(--sz-font-body)",
  fontSize: "var(--sz-body-md)",
  fontWeight: 600,
  color: "var(--sz-text-primary)",
  lineHeight: 1.3,
  flex: 1,
};

export const itemSubtitle: CSSProperties = {
  fontFamily: "var(--sz-font-body)",
  fontSize: "var(--sz-body-sm)",
  color: "var(--sz-text-secondary)",
  lineHeight: 1.4,
};

// ─── Status pill ───────────────────────────────────────────────

export const statusBase: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "10px",
  letterSpacing: "1px",
  textTransform: "uppercase",
  padding: "2px 8px",
  borderRadius: "var(--sz-radius-sm)",
  border: "1px solid var(--sz-border-dim)",
  color: "var(--sz-text-muted)",
  alignSelf: "flex-start",
  whiteSpace: "nowrap",
  lineHeight: 1.4,
};

export const statusBacklog: CSSProperties = {
  ...statusBase,
};

export const statusQueued: CSSProperties = {
  ...statusBase,
  borderColor: "var(--sz-text-secondary)",
  color: "var(--sz-text-secondary)",
};

export const statusActive: CSSProperties = {
  ...statusBase,
  borderColor: "var(--sz-success)",
  color: "var(--sz-success-text)",
};

export const statusInReview: CSSProperties = {
  ...statusBase,
  borderColor: "var(--sz-amber)",
  color: "var(--sz-amber)",
};

export const statusApproved: CSSProperties = {
  ...statusBase,
  borderColor: "var(--sz-success)",
  color: "var(--sz-success-text)",
  background: "var(--sz-success-bg, transparent)",
};

export const statusLive: CSSProperties = {
  ...statusBase,
  borderColor: "var(--sz-success)",
  color: "var(--sz-success-text)",
  fontWeight: 700,
};

export const statusSuspended: CSSProperties = {
  ...statusBase,
  borderColor: "var(--sz-warning)",
  color: "var(--sz-warning-text, var(--sz-warning))",
};

export const statusResolvedWin: CSSProperties = {
  ...statusBase,
  borderColor: "var(--sz-success)",
  color: "var(--sz-success-text)",
};

export const statusResolvedLoss: CSSProperties = {
  ...statusBase,
  borderColor: "var(--sz-danger)",
  color: "var(--sz-danger-text)",
};

export const statusResolvedVoid: CSSProperties = {
  ...statusBase,
  borderColor: "var(--sz-text-muted)",
  color: "var(--sz-text-muted)",
};

export const statusArchived: CSSProperties = {
  ...statusBase,
  opacity: 0.5,
};

// Phase 10 — 6 new lifecycle statuses

export const statusDetected: CSSProperties = {
  ...statusBase,
  background: "rgba(168, 85, 247, 0.10)",
  borderColor: "rgba(168, 85, 247, 0.3)",
  color: "rgba(168, 85, 247, 0.9)",
};

export const statusQualifying: CSSProperties = {
  ...statusBase,
  background: "rgba(59, 130, 246, 0.10)",
  borderColor: "rgba(59, 130, 246, 0.3)",
  color: "var(--sz-info-text)",
};

export const statusReady: CSSProperties = {
  ...statusBase,
  background: "rgba(34, 197, 94, 0.10)",
  borderColor: "rgba(34, 197, 94, 0.3)",
  color: "var(--sz-success-text)",
};

export const statusExecuting: CSSProperties = {
  ...statusBase,
  background: "rgba(245, 158, 11, 0.15)",
  borderColor: "var(--sz-amber)",
  color: "var(--sz-amber)",
};

export const statusMonitoring: CSSProperties = {
  ...statusBase,
  background: "rgba(59, 130, 246, 0.10)",
  borderColor: "rgba(59, 130, 246, 0.3)",
  color: "var(--sz-info-text)",
};

export const statusLearned: CSSProperties = {
  ...statusBase,
  background: "rgba(168, 85, 247, 0.08)",
  borderColor: "rgba(168, 85, 247, 0.2)",
  color: "rgba(168, 85, 247, 0.7)",
  opacity: 0.8,
};

// ─── Priority badge ────────────────────────────────────────────

export const priorityBadge: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "9px",
  letterSpacing: "1.5px",
  textTransform: "uppercase",
  fontWeight: 700,
  lineHeight: 1,
};

export const priorityCriticalText: CSSProperties = {
  ...priorityBadge,
  color: "var(--sz-danger-text)",
};

export const priorityHighText: CSSProperties = {
  ...priorityBadge,
  color: "var(--sz-amber)",
};

export const priorityMediumText: CSSProperties = {
  ...priorityBadge,
  color: "var(--sz-info)",
};

export const priorityLowText: CSSProperties = {
  ...priorityBadge,
  color: "var(--sz-text-muted)",
};

// ─── Value cells (quantitative data) ──────────────────────────

export const valueRow: CSSProperties = {
  display: "flex",
  gap: "var(--sz-space-3)",
  alignItems: "baseline",
};

export const valueCell: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "1px",
};

export const valueNumber: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-sm)",
  fontWeight: 600,
  color: "var(--sz-text-primary)",
  letterSpacing: "-0.3px",
};

export const valueLabel: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "9px",
  letterSpacing: "1.5px",
  textTransform: "uppercase",
  color: "var(--sz-text-muted)",
};

// ─── Tags ──────────────────────────────────────────────────────

export const tagRow: CSSProperties = {
  display: "flex",
  gap: "4px",
  flexWrap: "wrap",
};

export const tagPill: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "9px",
  letterSpacing: "1px",
  textTransform: "uppercase",
  padding: "1px 6px",
  borderRadius: "var(--sz-radius-sm)",
  background: "var(--sz-surface-recessed)",
  color: "var(--sz-text-secondary)",
};

// ─── Meta row (date, market, fixture) ──────────────────────────

export const metaRow: CSSProperties = {
  display: "flex",
  gap: "var(--sz-space-3)",
  alignItems: "center",
  flexWrap: "wrap",
};

export const metaItem: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-xs)",
  color: "var(--sz-text-muted)",
  display: "flex",
  alignItems: "center",
  gap: "4px",
};

export const metaDot: CSSProperties = {
  width: "3px",
  height: "3px",
  borderRadius: "50%",
  background: "var(--sz-text-muted)",
  flexShrink: 0,
};

// ─── Progress bar (for value_pct) ──────────────────────────────

export const progressRow: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "var(--sz-space-2)",
};

export const progressBarOuter: CSSProperties = {
  flex: 1,
  height: "4px",
  borderRadius: "2px",
  background: "var(--sz-surface-recessed)",
  overflow: "hidden",
};

export const progressBarInner: CSSProperties = {
  height: "100%",
  borderRadius: "2px",
  background: "var(--sz-amber)",
  transition: "width 0.3s ease",
};

export const progressValue: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-xs)",
  color: "var(--sz-text-secondary)",
  minWidth: "32px",
  textAlign: "right",
};

// ─── Approval banner ───────────────────────────────────────────

export const approvalBanner: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "var(--sz-space-2)",
  padding: "var(--sz-space-2) var(--sz-space-3)",
  borderRadius: "var(--sz-radius-sm)",
  background: "var(--sz-surface-recessed)",
  borderLeft: "3px solid var(--sz-amber)",
};

export const approvalText: CSSProperties = {
  fontFamily: "var(--sz-font-body)",
  fontSize: "var(--sz-body-sm)",
  color: "var(--sz-text-secondary)",
  flex: 1,
};

export const approvalAction: CSSProperties = {
  ...transitionFast,
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-xs)",
  letterSpacing: "1px",
  textTransform: "uppercase",
  padding: "4px 12px",
  borderRadius: "var(--sz-radius-sm)",
  border: "1px solid var(--sz-amber)",
  background: "transparent",
  color: "var(--sz-amber)",
  cursor: "pointer",
  fontWeight: 600,
};

export const approvalReject: CSSProperties = {
  ...approvalAction,
  borderColor: "var(--sz-text-muted)",
  color: "var(--sz-text-muted)",
};

// ─── Activity log ──────────────────────────────────────────────

export const activityRoot: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "var(--sz-space-1)",
};

export const activityEntry: CSSProperties = {
  display: "flex",
  gap: "var(--sz-space-2)",
  alignItems: "baseline",
  padding: "var(--sz-space-1) 0",
  borderBottom: "1px solid var(--sz-border-subtle)",
};

export const activityTimestamp: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "10px",
  color: "var(--sz-text-muted)",
  minWidth: "100px",
  flexShrink: 0,
};

export const activityAction: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-xs)",
  letterSpacing: "1px",
  textTransform: "uppercase",
  color: "var(--sz-text-secondary)",
  minWidth: "80px",
  flexShrink: 0,
};

export const activityActor: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "10px",
  padding: "1px 6px",
  borderRadius: "var(--sz-radius-sm)",
  background: "var(--sz-surface-recessed)",
  color: "var(--sz-text-muted)",
  textTransform: "uppercase",
  letterSpacing: "1px",
};

export const activitySummary: CSSProperties = {
  fontFamily: "var(--sz-font-body)",
  fontSize: "var(--sz-body-sm)",
  color: "var(--sz-text-primary)",
  flex: 1,
};

export const activitySeverity: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "9px",
  padding: "1px 5px",
  borderRadius: "var(--sz-radius-sm)",
  textTransform: "uppercase",
  letterSpacing: "1px",
  flexShrink: 0,
};

export const activityTag: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "9px",
  padding: "1px 4px",
  borderRadius: "var(--sz-radius-sm)",
  background: "var(--sz-surface-recessed)",
  color: "var(--sz-text-muted)",
  letterSpacing: "0.5px",
};

export const activityMeta: CSSProperties = {
  display: "flex",
  gap: "var(--sz-space-1)",
  alignItems: "center",
  flexWrap: "wrap",
};

// ─── Tab bar (board filter / view switcher) ────────────────────

export const tabBar: CSSProperties = {
  display: "flex",
  gap: "2px",
  borderBottom: "1px solid var(--sz-border-dim)",
  overflowX: "auto",
};

export const tabItem: CSSProperties = {
  ...transitionFast,
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-xs)",
  letterSpacing: "1.5px",
  textTransform: "uppercase",
  padding: "8px 16px",
  borderTop: "none",
  borderRight: "none",
  borderLeft: "none",
  borderBottom: "2px solid transparent",
  background: "transparent",
  color: "var(--sz-text-muted)",
  cursor: "pointer",
  whiteSpace: "nowrap",
};

export const tabItemActive: CSSProperties = {
  ...tabItem,
  color: "var(--sz-text-primary)",
  borderBottomColor: "var(--sz-amber)",
};

export const tabCount: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "10px",
  color: "var(--sz-text-muted)",
  marginLeft: "6px",
};

// ─── Board grid / list layouts ─────────────────────────────────

export const boardGrid: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
  gap: "var(--sz-space-3)",
};

export const boardList: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "var(--sz-space-2)",
};

// ─── Kanban layout ─────────────────────────────────────────────

export const kanbanRoot: CSSProperties = {
  display: "flex",
  gap: "var(--sz-space-3)",
  overflowX: "auto",
  paddingBottom: "var(--sz-space-2)",
};

export const kanbanLane: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "var(--sz-space-2)",
  minWidth: "280px",
  maxWidth: "340px",
  flex: "0 0 300px",
};

// ─── Empty state ───────────────────────────────────────────────

export const emptyState: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: "var(--sz-space-2)",
  padding: "var(--sz-space-6) var(--sz-space-4)",
  borderRadius: "var(--sz-radius-md)",
  border: "1px dashed var(--sz-border-dim)",
};

export const emptyTitle: CSSProperties = {
  fontFamily: "var(--sz-font-display)",
  fontSize: "1.2rem",
  letterSpacing: "2px",
  textTransform: "uppercase",
  color: "var(--sz-text-muted)",
};

export const emptyNote: CSSProperties = {
  fontFamily: "var(--sz-font-body)",
  fontSize: "var(--sz-body-sm)",
  color: "var(--sz-text-muted)",
  textAlign: "center",
  maxWidth: "280px",
};

// ─── Phantom badge ─────────────────────────────────────────────

export const phantomBadge: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-xs)",
  letterSpacing: "2px",
  textTransform: "uppercase",
  padding: "2px 8px",
  borderRadius: "var(--sz-radius-sm)",
  border: "1px solid var(--sz-text-muted)",
  color: "var(--sz-text-muted)",
  alignSelf: "flex-start",
};

// ─── Section divider ───────────────────────────────────────────

export const sectionDivider: CSSProperties = {
  borderTop: "1px solid var(--sz-border-subtle)",
  paddingTop: "var(--sz-space-3)",
  marginTop: "var(--sz-space-1)",
};

// ─── Stat card (dashboard KPI) ─────────────────────────────────

export const statCard: CSSProperties = {
  ...cardLow,
  display: "flex",
  flexDirection: "column",
  gap: "2px",
  padding: "var(--sz-space-3)",
  borderRadius: "var(--sz-radius-md)",
  background: "var(--sz-surface-card)",
  borderTop: "2px solid var(--sz-amber)",
};

export const statValue: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-lg, 1.5rem)",
  fontWeight: 700,
  color: "var(--sz-text-primary)",
  letterSpacing: "-0.5px",
};

export const statLabel: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "9px",
  letterSpacing: "1.5px",
  textTransform: "uppercase",
  color: "var(--sz-text-muted)",
};

export const statRow: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
  gap: "var(--sz-space-2)",
};
